interface PublicEnv {
  VITE_SUPABASE_URL: string;
  VITE_SUPABASE_ANON_KEY: string;
  VITE_API_URL: string;
  VITE_LOCAL_INBOX_URL?: string;
  VITE_EMAIL_AUTH_MODE?: 'magic_link' | 'password';
  VITE_GOOGLE_AUTH_ENABLED?: 'true' | 'false';
  VITE_TELEGRAM_AUTH_ENABLED?: 'true' | 'false';
  VITE_TELEGRAM_AUTH_CLIENT_ID?: string;
  VITE_ANALYTICS_ENABLED?: 'true' | 'false';
  VITE_ANALYTICS_MARKET?: 'ru' | 'global';
  VITE_YANDEX_METRIKA_ID?: string;
  VITE_GOOGLE_ANALYTICS_ID?: string;
}

function requiredString(environment: Record<string, unknown>, key: string): string {
  const value = environment[key];
  if (typeof value !== 'string' || value.length === 0) {
    throw new Error(`Missing public environment variable: ${key}`);
  }
  return value;
}

function optionalString(environment: Record<string, unknown>, key: string): string | undefined {
  const value = environment[key];
  if (value === undefined || value === '') return undefined;
  if (typeof value !== 'string') throw new Error(`Invalid public environment variable: ${key}`);
  return value;
}

function url(environment: Record<string, unknown>, key: string, required: true): string;
function url(
  environment: Record<string, unknown>,
  key: string,
  required: false
): string | undefined;
function url(
  environment: Record<string, unknown>,
  key: string,
  required: boolean
): string | undefined {
  const value = required ? requiredString(environment, key) : optionalString(environment, key);
  if (value === undefined) return undefined;
  try {
    new URL(value);
  } catch {
    throw new Error(`Invalid URL in public environment variable: ${key}`);
  }
  return value;
}

function optionalEnum<const T extends readonly string[]>(
  environment: Record<string, unknown>,
  key: string,
  allowed: T
): T[number] | undefined {
  const value = optionalString(environment, key);
  if (value === undefined) return undefined;
  if (!allowed.includes(value)) {
    throw new Error(`Invalid public environment variable: ${key}`);
  }
  return value as T[number];
}

export function parsePublicEnv(environment: Record<string, unknown>): PublicEnv {
  const result: PublicEnv = {
    VITE_SUPABASE_URL: url(environment, 'VITE_SUPABASE_URL', true),
    VITE_SUPABASE_ANON_KEY: requiredString(environment, 'VITE_SUPABASE_ANON_KEY'),
    VITE_API_URL: url(environment, 'VITE_API_URL', true),
  };
  const localInboxUrl = url(environment, 'VITE_LOCAL_INBOX_URL', false);
  const emailAuthMode = optionalEnum(environment, 'VITE_EMAIL_AUTH_MODE', [
    'magic_link',
    'password',
  ] as const);
  const googleAuthEnabled = optionalEnum(environment, 'VITE_GOOGLE_AUTH_ENABLED', [
    'true',
    'false',
  ] as const);
  const telegramAuthEnabled = optionalEnum(environment, 'VITE_TELEGRAM_AUTH_ENABLED', [
    'true',
    'false',
  ] as const);
  const telegramAuthClientId = optionalString(environment, 'VITE_TELEGRAM_AUTH_CLIENT_ID');
  if (telegramAuthClientId !== undefined && !/^\d+$/u.test(telegramAuthClientId)) {
    throw new Error('Invalid public environment variable: VITE_TELEGRAM_AUTH_CLIENT_ID');
  }
  if (telegramAuthEnabled === 'true' && telegramAuthClientId === undefined) {
    throw new Error('Missing public environment variable: VITE_TELEGRAM_AUTH_CLIENT_ID');
  }
  const analyticsEnabled = optionalEnum(environment, 'VITE_ANALYTICS_ENABLED', [
    'true',
    'false',
  ] as const);
  const analyticsMarket = optionalEnum(environment, 'VITE_ANALYTICS_MARKET', [
    'ru',
    'global',
  ] as const);
  const yandexMetrikaId = optionalString(environment, 'VITE_YANDEX_METRIKA_ID');
  const googleAnalyticsId = optionalString(environment, 'VITE_GOOGLE_ANALYTICS_ID');
  if (localInboxUrl !== undefined) result.VITE_LOCAL_INBOX_URL = localInboxUrl;
  if (emailAuthMode !== undefined) result.VITE_EMAIL_AUTH_MODE = emailAuthMode;
  if (googleAuthEnabled !== undefined) result.VITE_GOOGLE_AUTH_ENABLED = googleAuthEnabled;
  if (telegramAuthEnabled !== undefined) {
    result.VITE_TELEGRAM_AUTH_ENABLED = telegramAuthEnabled;
  }
  if (telegramAuthClientId !== undefined) {
    result.VITE_TELEGRAM_AUTH_CLIENT_ID = telegramAuthClientId;
  }
  if (analyticsEnabled !== undefined) result.VITE_ANALYTICS_ENABLED = analyticsEnabled;
  if (analyticsMarket !== undefined) result.VITE_ANALYTICS_MARKET = analyticsMarket;
  if (yandexMetrikaId !== undefined) result.VITE_YANDEX_METRIKA_ID = yandexMetrikaId;
  if (googleAnalyticsId !== undefined) result.VITE_GOOGLE_ANALYTICS_ID = googleAnalyticsId;
  return result;
}

const nonEmptyPublicEnvironment = Object.fromEntries(
  Object.entries(import.meta.env).filter(([, value]) => value !== '')
);

export const publicEnv = parsePublicEnv(nonEmptyPublicEnvironment);
